import { fetchManagementFees } from 'views/Accounts/components/ManagementFees/services/fetchManagementFees';
import { ToolExecutionResult } from '../types';
import { formatCurrency } from '../utils/formatters';

export const executeGetManagementFees = async (args: Record<string, unknown>): Promise<ToolExecutionResult> => {
  try {
    const limit = (args.limit as number) || 20;
    const response = await fetchManagementFees();

    if (!response.data || response.data.length === 0) {
      return {
        success: true,
        data: {
          message: 'No management fees found.',
          fees: [],
          count: 0,
        },
      };
    }

    // Sort by date descending (most recent first)
    const sortedFees = [...response.data].sort(
      (a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime(),
    );

    const fees = sortedFees.slice(0, limit).map((fee) => ({
      portfolioName: fee.portfolioName,
      feeType: fee.feeType,
      feeAmount: formatCurrency(fee.feeAmount),
      portfolioValue: formatCurrency(fee.portfolioValue),
      appliedRate: `${fee.appliedPct}%`,
      invoiceNumber: fee.invoiceNumber,
      invoiceDate: fee.invoiceDate,
      valuationDate: fee.valuationDate,
      status: fee.status,
    }));

    // Calculate summary stats
    const totalFees = response.data.reduce((sum, f) => sum + f.feeAmount, 0);
    const paidFees = response.data.filter((f) => f.status === 'Paid').reduce((sum, f) => sum + f.feeAmount, 0);
    const unpaidFees = response.data.filter((f) => f.status !== 'Paid').reduce((sum, f) => sum + f.feeAmount, 0);

    // Group by fee type
    const feesByType: Record<string, number> = {};
    response.data.forEach((fee) => {
      feesByType[fee.feeType] = (feesByType[fee.feeType] || 0) + fee.feeAmount;
    });

    return {
      success: true,
      data: {
        fees,
        count: response.data.length,
        summary: {
          totalFees: formatCurrency(totalFees),
          paidFees: formatCurrency(paidFees),
          unpaidFees: formatCurrency(unpaidFees),
          feesByType: Object.entries(feesByType).map(([type, amount]) => ({
            type,
            amount: formatCurrency(amount),
          })),
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to fetch management fees: ${(error as Error).message}`,
    };
  }
};
